// Pure derivation of the embed wizard's step order from a resolved
// form's `section_order` (docs/MULTI_FORM.md §4.3 / Lane 2).
//
// Pulled out of src/embed/index.ts — like embed-shop-config.ts — so
// vitest can exercise the ordering + fallback rules without standing
// up the embed's shadow-DOM / currentScript globals. The embed bundle
// imports resolveWizardSteps() and maps the returned ids onto its
// renderers. Consumes Lane 1's schema (FormSectionId + the section
// vocabulary + normalizeSectionOrder) — never mutates it.
import {
  type FormSectionId,
  normalizeSectionOrder,
} from "@/lib/shop-config-types";

/** The embed wizard's internal step ids. These are the LEGACY step
 *  names the renderer has always used (`about`/`shipping`/`quote`/
 *  `finish`), plus the NEW `describe` step promoted from the old
 *  describe-mode toggle. Distinct from Lane 1's FormSectionId vocabulary
 *  — resolveWizardSteps() is the mapping between the two:
 *    contact  → about      products → quote       describe → describe
 *    shipping → shipping    finish   → finish      consent  → (no step) */
export type WizardStepId = "about" | "shipping" | "quote" | "describe" | "finish";

/** section id → wizard step id. `consent` + `finish` are handled
 *  specially in resolveWizardSteps (consent is not a standalone step;
 *  finish is always appended last), so they're intentionally absent. */
const SECTION_TO_STEP: Record<
  Exclude<FormSectionId, "consent" | "finish">,
  WizardStepId
> = {
  contact: "about",
  shipping: "shipping",
  products: "quote",
  describe: "describe",
};

/** Today's hardcoded order, used verbatim when a form carries no
 *  `section_order` (the default / un-migrated form). Products-first:
 *  the customer sees what they're configuring before being asked for
 *  info. BACK-COMPAT is non-negotiable — this must stay byte-identical
 *  to the pre-multi-form `stepDefs`. */
function fallbackOrder(shippingEnabled: boolean): WizardStepId[] {
  const out: WizardStepId[] = ["quote", "about"];
  if (shippingEnabled) out.push("shipping");
  out.push("finish");
  return out;
}

/** Ordered wizard step ids for a resolved form.
 *
 *  - `section_order` ABSENT / invalid → today's exact order (fallback).
 *  - `section_order` PRESENT → mapped id-by-id:
 *      · `contact`/`products`/`describe` → their step.
 *      · `shipping` → the shipping step ONLY when `shippingEnabled`
 *        (the section-level Require-shipping gate still wins — a
 *        shipping entry with the toggle off doesn't render).
 *      · `consent` → skipped (consent renders inside the finish area,
 *        gated by consent_enabled; it is never a standalone step).
 *      · `finish` → dropped here and re-appended LAST, always, even if a
 *        malformed order put it elsewhere (FORM_SECTION_TERMINAL).
 *  `section_order` is run through normalizeSectionOrder first so a raw /
 *  cached / hand-set value gets the same dedupe + known-id + terminal
 *  guarantees the persisted config gets. */
export function resolveWizardSteps(
  sectionOrder: FormSectionId[] | undefined | null,
  shippingEnabled: boolean,
): WizardStepId[] {
  const normalized = normalizeSectionOrder(sectionOrder ?? undefined);
  if (!normalized) return fallbackOrder(shippingEnabled);

  const out: WizardStepId[] = [];
  for (const sec of normalized) {
    // Literal comparisons (not FORM_SECTION_TERMINAL) so TS narrows the
    // union — finish is re-appended last, consent is never a step.
    if (sec === "finish") continue;
    if (sec === "consent") continue;
    if (sec === "shipping") {
      if (shippingEnabled) out.push("shipping");
      continue;
    }
    out.push(SECTION_TO_STEP[sec]);
  }
  out.push("finish"); // FORM_SECTION_TERMINAL — always last
  return out;
}
